/**
 * Browser-safe request context store.
 * On the server, AsyncLocalStorage registers its getter via registerGetStore().
 * On the browser, getRequestContext() always returns undefined (unused by apiClientBrowser).
 */
interface RequestContextStore {
  origin: string;
  hostname: string;
  languageCode: string | undefined;
  // Mutable holder: server-only components (ProtectedRoute) can request a redirect
  // during SSR render, which the server issues as a 302 after render — see setAuthRedirect.
  authRedirect?: { to?: string };
}

type GetStoreFn = () => RequestContextStore | undefined;

let getStoreFn: GetStoreFn = () => undefined;

export const registerGetStore = (fn: GetStoreFn): void => {
  getStoreFn = fn;
};

export const getRequestContext = (): RequestContextStore | undefined => getStoreFn();

/**
 * Server-only in practice: records the URL a protected route wants to redirect an
 * unauthenticated request to, so the SSR server can respond with a 302 instead of
 * painting the protected shell. No-op on the browser (no request context registered).
 */
export const setAuthRedirect = (to: string): void => {
  const store = getStoreFn();
  if (store?.authRedirect) {
    store.authRedirect.to = to;
  }
};

export const getAuthRedirect = (): string | undefined => getStoreFn()?.authRedirect?.to;
